import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { DateProvider } from '../../../providers';
import LogoImg from './logo.svg';
import {
  Container,
  CupLeft,
  CupRight,
  Logo,
  PointsContainer,
  PointsItem,
  TeamName,
  TeamNameAway,
  Timer,
} from './styles';

function SoccerCopaRey2023({ data }) {
  const { awayTeam, homeTeam, status, time } = data;
  const [, refresh] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => refresh(Date.now()), 1000);
    if (status === 'paused') clearInterval(interval);
    return () => {
      clearInterval(interval);
    };
  }, [status]);

  const getTime = () => {
    if (time.length === 0) return '00:00';
    const [minutes, seconds] = DateProvider.getSoccerTime(time);
    return `${minutes}:${seconds}`;
  };

  return (
    <>
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <Container>
        <svg
          width="24"
          height="46"
          viewBox="0 0 24 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="12" width="12" height="46" fill="#232335" />
          <path
            d="M0 0H7V35.6158C10.8946 44.6621 15.1246 45.6665 17 45.659C8.52901 46.757 3.84878 45.6499 0 35.6158V0Z"
            fill={homeTeam?.colors?.[0]?.value || '#AC0D4C'}
          />
          <path
            d="M7 0H14V35.6158C17.8946 44.6621 22.1246 45.6665 24 45.659C15.529 46.757 10.8488 45.6499 7 35.6158V0Z"
            fill={homeTeam?.colors?.[1]?.value || 'black'}
          />
        </svg>
        <TeamName>{homeTeam.name}</TeamName>
        <CupLeft
          width="16"
          height="46"
          viewBox="0 0 16 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1 0C0.79541 3.72529e-09 0.690159 1.15755 0.719071 9.14789C0.771112 23.2039 3.81059 43.3851 16.0001 46.0001V0C1.5 0 1.20459 -3.72529e-09 1 0ZM15 43.0001C16.4375 42.1118 15.4061 10.2999 13.9478 7.57481C13.2887 6.3441 12.3785 5.37943 11.924 5.42917C8.72411 5.78311 8.20601 7.16303 8.20601 15.3327C8.20601 27.7542 12.1077 44.7883 15 43.0001Z"
            fill="#EA3545"
          />
        </CupLeft>
        <PointsContainer>
          <PointsItem>{homeTeam.points}</PointsItem>
          <Logo alt="Logo Copa del Rey" src={LogoImg} />
          <PointsItem>{awayTeam.points}</PointsItem>
        </PointsContainer>
        <CupRight
          width="16"
          height="46"
          viewBox="0 0 16 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15 0C15.2046 3.72529e-09 15.3098 1.15755 15.2809 9.14789C15.2289 23.2039 12.1894 43.3851 -5.05447e-05 46.0001V0C14.5 0 14.7954 -3.72529e-09 15 0ZM0.999959 43.0001C-0.437524 42.1118 0.593853 10.2999 2.05215 7.57481C2.71134 6.3441 3.62147 5.37943 4.07596 5.42917C7.27589 5.78311 7.79399 7.16303 7.79399 15.3327C7.79399 27.7542 3.89227 44.7883 0.999959 43.0001Z"
            fill="#EA3545"
          />
        </CupRight>
        <TeamNameAway>{awayTeam.name}</TeamNameAway>
        <svg
          width="24"
          height="46"
          viewBox="0 0 24 46"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="11"
            height="46"
            transform="matrix(-1 0 0 1 11 0)"
            fill="#232335"
          />
          <path
            d="M24 0H17V35.6158C13.1054 44.6621 8.87537 45.6665 7 45.659C15.471 46.757 20.1512 45.6499 24 35.6158V0Z"
            fill={awayTeam?.colors?.[1]?.value || '#0D539D'}
          />
          <path
            d="M17 0H10V35.6158C6.1054 44.6621 1.87537 45.6665 0 45.659C8.47099 46.757 13.1512 45.6499 17 35.6158V0Z"
            fill={awayTeam?.colors?.[0]?.value || 'white'}
          />
        </svg>

        <Timer>{getTime()}</Timer>
      </Container>
    </>
  );
}

SoccerCopaRey2023.propTypes = {
  data: PropTypes.shape({
    awayTeam: PropTypes.shape({
      colors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      image: PropTypes.string,
      name: PropTypes.string,
      points: PropTypes.number,
    }),
    homeTeam: PropTypes.shape({
      colors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      image: PropTypes.string,
      name: PropTypes.string,
      points: PropTypes.number,
    }),
    status: PropTypes.string,
    time: PropTypes.arrayOf(PropTypes.number),
  }),
  styles: PropTypes.shape({}),
};

SoccerCopaRey2023.defaultProps = {
  data: {
    awayTeam: {
      colors: ['#74ACDF'],
      image:
        'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/Flag_of_Argentina.svg/1200px-Flag_of_Argentina.svg.png',
      name: 'ARG',
      points: 1,
    },
    homeTeam: {
      colors: ['#E50A38'],
      image:
        'https://www.banderasphonline.com/wp-content/uploads/2021/08/comprar-bandera-espana-sin-escudo-para-exterior-interior-1200x900.png',
      name: 'ESP',
      points: 0,
    },
    status: 'paused',
    time: [],
  },
  styles: {},
};

export default SoccerCopaRey2023;
